import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useItem, useItemRelistPrice, useItemRelistBuyer } from '@/hooks/item';
import { ItemRelistBuyerModal } from '@/types/modal';
import { Switch } from '@/components/molecules/common/Form';
import { CopyModal, ErrorModal, LoadingModal, StandardModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: ItemRelistBuyerModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;
  const [charge, setCharge] = useState<boolean>(true);
  const itemR = useItem(item.productId, false);
  const submitR = useItemRelistBuyer(item.productId);
  const priceR = useItemRelistPrice(item.productId);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  if (priceR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.item_relist_buyer.title')}
        onClose={onClose}
      />
    );
  } else if (priceR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_relist_buyer.title')}
        error={priceR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.item_relist_buyer.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <CopyModal
        title={t('modal.item_relist_buyer.title_success')}
        beforeInfo={
          <>
            <div className={styles.paddingY1rem}>{t('modal.item_relist_buyer.new_item_created')}</div>
            <div className={styles.height05rem} />
            <div className={clsx([styles.text075rem, styles.textGrey])}>
              {t('modal.item_relist_buyer.new_item_number')}
            </div>
            <div className={styles.height05rem} />
          </>
        }
        afterInfo={<br />}
        textToCopy={String(submitR.data.data.newIdProduct)}
        onClose={onClose}
      />
    );
  }

  const { publicPrice, sellerPrice, fee } = charge ? priceR.data.data.withRelistFee : priceR.data.data.noRelistFee;

  const body = (
    <div className={styles.itemRelistBuyer}>
      <div className={styles.height1rem} />
      <div className={styles.text1rem}>{t('modal.item_relist_buyer.description')}</div>
      <div className={styles.height1rem} />
      <Switch
        disabled={false}
        label={charge ? t('modal.item_relist_buyer.charge_on') : t('modal.item_relist_buyer.charge_off')}
        value={charge}
        onChange={setCharge}
      />
      <div className={clsx([styles.text075rem, styles.textGrey])}>
        {t('modal.item_relist_buyer.charge_description')}
      </div>
      <div className={styles.height1rem} />
      <div className={styles.itemRelistBuyerRow}>
        <div className={styles.text0875rem}>{t('modal.item_relist_buyer.selling_price')}</div>
        <div className={styles.text0875rem}>{publicPrice}</div>
      </div>
      <div className={styles.itemRelistBuyerRow}>
        <div className={styles.text0875rem}>{t('modal.item_relist_buyer.selling_fee')}</div>
        <div className={styles.text0875rem}>{fee}</div>
      </div>
      <div className={clsx([styles.itemRelistBuyerRow, styles.itemRelistBuyerRowNoBorder])}>
        <div className={styles.text0875rem}>{t('modal.item_relist_buyer.seller_will_get')}</div>
        <div className={styles.text0875rem}>{sellerPrice}</div>
      </div>
      <div className={styles.height1rem} />
    </div>
  );

  return (
    <StandardModal
      title={t('modal.item_relist_buyer.title')}
      onClose={onClose}
      onSubmit={() =>
        submitR.mutate({
          orderId: Number(item.orderId),
          buyerId: Number(item.buyerId),
          relistFee: charge,
        })
      }
      submitLabel={t('common.relist').toUpperCase()}
      submittable={submitR.kind !== 'Loading'}
      body={body}
      containerStyle={{ width: 500 }}
      modalName="item_relist_buyer"
    />
  );
};

export default View;
